import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';

// ─── Types ───────────────────────────────────────────────────────────────────
interface CalcInputs {
  // Step 1 — About You
  age: number;
  gender: 'male' | 'female';
  smoker: 'no' | 'yes';
  health: 'excellent' | 'good' | 'fair' | 'poor';
  // Step 2 — Income & Dependents
  annualIncome: number;
  yearsToRetirement: number;
  spouseIncome: number;
  numChildren: number;
  youngestChildAge: number;
  // Step 3 — Debts & Obligations
  mortgageBalance: number;
  otherDebts: number;
  finalExpenses: number;
  // Step 4 — Assets & Existing Coverage
  savings: number;
  existingCoverage: number;
  // Step 5 — Goals
  educationPerChild: number;
  incomeReplaceYears: number;
}

interface CalcResults {
  incomeReplacement: number;
  debtPayoff: number;
  educationFund: number;
  finalExpenses: number;
  totalNeed: number;
  existingAssets: number;
  coverageGap: number;
  recommendedPolicy: string;
  recommendedTerm: number;
  estimatedMonthlyPremium: number;
  riskClass: string;
}

const DEFAULT_INPUTS: CalcInputs = {
  age: 35,
  gender: 'male',
  smoker: 'no',
  health: 'good',
  annualIncome: 75000,
  yearsToRetirement: 30,
  spouseIncome: 0,
  numChildren: 2,
  youngestChildAge: 5,
  mortgageBalance: 185000,
  otherDebts: 25000,
  finalExpenses: 12000,
  savings: 30000,
  existingCoverage: 75000,
  educationPerChild: 100000,
  incomeReplaceYears: 15,
};

const STEPS = [
  { id: 1, label: 'About You', icon: 'ri-user-line' },
  { id: 2, label: 'Income & Family', icon: 'ri-team-line' },
  { id: 3, label: 'Debts', icon: 'ri-bank-card-line' },
  { id: 4, label: 'Assets', icon: 'ri-safe-line' },
  { id: 5, label: 'Goals', icon: 'ri-flag-line' },
  { id: 6, label: 'Your Results', icon: 'ri-bar-chart-line' },
];

// ─── Calculation Engine ───────────────────────────────────────────────────────
function calculate(inputs: CalcInputs): CalcResults {
  const incomeReplacement = inputs.annualIncome * inputs.incomeReplaceYears;
  const debtPayoff = inputs.mortgageBalance + inputs.otherDebts;
  const educationFund = inputs.numChildren * inputs.educationPerChild;
  const finalExpenses = inputs.finalExpenses;

  const totalNeed = incomeReplacement + debtPayoff + educationFund + finalExpenses;
  const existingAssets = inputs.savings + inputs.existingCoverage;
  const coverageGap = Math.max(0, totalNeed - existingAssets);

  // Policy recommendation
  const yearsUntilKidsGrown = inputs.numChildren > 0 ? Math.max(0, 22 - inputs.youngestChildAge) : 0;
  const yearsToRetirement = inputs.yearsToRetirement;
  const termNeeded = Math.max(yearsUntilKidsGrown, Math.min(yearsToRetirement, 30));
  let recommendedTerm = 10;
  if (termNeeded >= 25) recommendedTerm = 30;
  else if (termNeeded >= 18) recommendedTerm = 20;
  else if (termNeeded >= 13) recommendedTerm = 15;
  else recommendedTerm = 10;

  let recommendedPolicy = 'Term Life Insurance';
  if (inputs.age >= 60 && coverageGap < 50000) recommendedPolicy = 'Final Expense Insurance';
  else if (inputs.age >= 55 && coverageGap < 100000) recommendedPolicy = 'Guaranteed Issue Life Insurance';
  else if (inputs.annualIncome > 150000 && inputs.yearsToRetirement > 20) recommendedPolicy = 'Term + Whole Life Blend';

  // Risk class
  let riskClass = 'Preferred Plus';
  if (inputs.smoker === 'yes') riskClass = 'Tobacco';
  else if (inputs.health === 'fair') riskClass = 'Standard';
  else if (inputs.health === 'poor') riskClass = 'Substandard';
  else if (inputs.health === 'good') riskClass = 'Preferred';

  // Monthly premium estimate (simplified actuarial table)
  const basePer1000: Record<string, Record<number, number>> = {
    'Preferred Plus': { 10: 0.08, 15: 0.10, 20: 0.13, 30: 0.18 },
    'Preferred':      { 10: 0.10, 15: 0.13, 20: 0.17, 30: 0.24 },
    'Standard':       { 10: 0.15, 15: 0.19, 20: 0.25, 30: 0.35 },
    'Tobacco':        { 10: 0.28, 15: 0.36, 20: 0.46, 30: 0.65 },
    'Substandard':    { 10: 0.22, 15: 0.28, 20: 0.36, 30: 0.50 },
  };
  const ageMultiplier = 1 + Math.max(0, (inputs.age - 30)) * 0.04;
  const genderMultiplier = inputs.gender === 'female' ? 0.82 : 1.0;
  const rateKey = riskClass in basePer1000 ? riskClass : 'Standard';
  const termKey = recommendedTerm as 10 | 15 | 20 | 30;
  const ratePerThousand = (basePer1000[rateKey][termKey] || 0.17) * ageMultiplier * genderMultiplier;
  const estimatedMonthlyPremium = Math.round((coverageGap / 1000) * ratePerThousand);

  return {
    incomeReplacement,
    debtPayoff,
    educationFund,
    finalExpenses,
    totalNeed,
    existingAssets,
    coverageGap,
    recommendedPolicy,
    recommendedTerm,
    estimatedMonthlyPremium,
    riskClass,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  n >= 1000000
    ? `$${(n / 1000000).toFixed(2)}M`
    : `$${n.toLocaleString()}`;

const SliderInput = ({
  label, value, min, max, step, prefix, suffix, onChange, hint,
}: {
  label: string; value: number; min: number; max: number; step: number;
  prefix?: string; suffix?: string; onChange: (v: number) => void; hint?: string;
}) => (
  <div className="mb-6">
    <div className="flex justify-between items-center mb-2">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <span className="text-base font-bold text-[#1A3A52]">
        {prefix}{value.toLocaleString()}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#D97642]"
    />
    <div className="flex justify-between text-xs text-gray-400 mt-1">
      <span>{prefix}{min.toLocaleString()}{suffix}</span>
      <span>{prefix}{max.toLocaleString()}{suffix}</span>
    </div>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function IndianaLifeInsuranceCalculator() {
  const [step, setStep] = useState(1);
  const [inputs, setInputs] = useState<CalcInputs>(DEFAULT_INPUTS);
  const [results, setResults] = useState<CalcResults | null>(null);
  const [animating, setAnimating] = useState(false);

  const set = useCallback(<K extends keyof CalcInputs>(key: K, val: CalcInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: val }));
  }, []);

  const goNext = () => {
    if (step === 5) {
      setResults(calculate(inputs));
    }
    setAnimating(true);
    setTimeout(() => {
      setStep(s => Math.min(s + 1, 6));
      setAnimating(false);
    }, 200);
  };

  const goBack = () => {
    setAnimating(true);
    setTimeout(() => {
      setStep(s => Math.max(s - 1, 1));
      setAnimating(false);
    }, 200);
  };

  const restart = () => {
    setStep(1);
    setInputs(DEFAULT_INPUTS);
    setResults(null);
  };

  useEffect(() => {
    document.title = 'Indiana Life Insurance Calculator — Free Needs Assessment Tool | Hoosier Life Insurance';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Use our free Indiana life insurance needs calculator to find exactly how much coverage your family needs. Enter your income, debts, family size, and goals — get a personalized recommendation in 2 minutes.');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'Indiana life insurance calculator, life insurance needs assessment Indiana, how much life insurance do I need Indiana, life insurance calculator Indianapolis');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/indiana-life-insurance-calculator`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Indiana Life Insurance Needs Calculator',
        description: 'Free interactive life insurance needs assessment calculator for Indiana families. Calculate your coverage gap, get a policy recommendation, and estimate your monthly premium.',
        url: `${SITE_URL}/resources/indiana-life-insurance-calculator`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'Hoosier Life Insurance' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Indiana Life Insurance Calculator', item: `${SITE_URL}/resources/indiana-life-insurance-calculator` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How accurate is this Indiana life insurance calculator?',
            acceptedAnswer: { '@type': 'Answer', text: 'This calculator uses the industry-standard DIME method (Debt, Income, Mortgage, Education) to estimate your coverage needs. Premium estimates are based on actuarial rate tables for Indiana residents and are accurate within 10-15% of actual carrier quotes. For a precise quote, speak with a licensed Indiana agent.' },
          },
          {
            '@type': 'Question',
            name: 'What information do I need to use this calculator?',
            acceptedAnswer: { '@type': 'Answer', text: 'You need your annual income, mortgage balance, other debts, number of children, savings/investments, and any existing life insurance coverage. The calculator takes about 2 minutes to complete.' },
          },
          {
            '@type': 'Question',
            name: 'Is my information saved or shared?',
            acceptedAnswer: { '@type': 'Answer', text: 'No. All calculations happen entirely in your browser. No personal information is stored or transmitted. Your data never leaves your device unless you choose to request a quote.' },
          },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(`schema-calc-${i}`) as HTMLScriptElement | null;
      if (!script) { script = document.createElement('script'); script.id = `schema-calc-${i}`; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      schemas.forEach((_, i) => document.getElementById(`schema-calc-${i}`)?.remove());
    };
  }, []);

  const progress = ((step - 1) / 5) * 100;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#D97642] blur-3xl" />
          <div className="absolute bottom-0 right-20 w-96 h-96 rounded-full bg-teal-400 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="mb-4">
            <Link to="/resources" className="inline-flex items-center text-white/60 hover:text-white transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2" />Back to Resources
            </Link>
          </div>
          <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] border border-[#D97642]/30 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
            <i className="ri-calculator-line" />Free Interactive Tool
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Indiana Life Insurance<br />Needs Calculator
          </h1>
          <p className="text-xl text-white/75 mb-8 max-w-2xl mx-auto leading-relaxed">
            Answer 5 quick questions and get a personalized coverage recommendation — including your exact coverage gap and estimated monthly premium.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/60">
            <span className="flex items-center gap-2"><i className="ri-time-line" />2 minutes</span>
            <span className="flex items-center gap-2"><i className="ri-lock-line" />100% private — no data stored</span>
            <span className="flex items-center gap-2"><i className="ri-map-pin-line" />Indiana-specific rates</span>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-3xl mx-auto px-6">

          {/* Step Progress */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              {STEPS.map((s, idx) => (
                <div key={s.id} className="flex items-center flex-1">
                  <div className={`flex flex-col items-center ${idx < STEPS.length - 1 ? 'flex-1' : ''}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      step > s.id ? 'bg-teal-600 text-white' :
                      step === s.id ? 'bg-[#D97642] text-white ring-4 ring-[#D97642]/20' :
                      'bg-gray-200 text-gray-400'
                    }`}>
                      {step > s.id ? <i className="ri-check-line" /> : <i className={s.icon} />}
                    </div>
                    <span className={`text-xs mt-1 font-medium hidden sm:block ${step === s.id ? 'text-[#D97642]' : step > s.id ? 'text-teal-600' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 transition-all duration-500 ${step > s.id ? 'bg-teal-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
              <div
                className="bg-gradient-to-r from-[#D97642] to-teal-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Card */}
          <div className={`bg-white rounded-2xl border border-gray-200 p-8 transition-all duration-200 ${animating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>

            {/* ── Step 1: About You ── */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Tell us about yourself</h2>
                <p className="text-gray-500 text-sm mb-8">This helps us calculate accurate Indiana premium estimates.</p>

                <SliderInput label="Your Age" value={inputs.age} min={18} max={75} step={1} suffix=" yrs" onChange={v => set('age', v)} hint="Younger = lower premiums. Lock in your rate now." />

                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-700 block mb-3">Gender</label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['male', 'female'] as const).map(g => (
                      <button
                        key={g}
                        onClick={() => set('gender', g)}
                        className={`py-3 rounded-xl border-2 font-semibold text-sm capitalize transition-all cursor-pointer ${inputs.gender === g ? 'border-[#D97642] bg-[#FDF6F0] text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <i className={`${g === 'male' ? 'ri-men-line' : 'ri-women-line'} mr-2`} />{g}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-700 block mb-3">Do you smoke or use tobacco?</label>
                  <div className="grid grid-cols-2 gap-3">
                    {([['no', 'Non-Smoker', 'ri-leaf-line'], ['yes', 'Smoker / Tobacco', 'ri-fire-line']] as const).map(([val, label, icon]) => (
                      <button
                        key={val}
                        onClick={() => set('smoker', val)}
                        className={`py-3 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer ${inputs.smoker === val ? 'border-[#D97642] bg-[#FDF6F0] text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <i className={`${icon} mr-2`} />{label}
                      </button>
                    ))}
                  </div>
                  {inputs.smoker === 'yes' && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <i className="ri-information-line" />Smokers pay 2-3x more. Quitting for 12 months can cut your rate significantly.
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-700 block mb-3">Overall Health</label>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      ['excellent', 'Excellent', 'ri-heart-pulse-line', 'No conditions, very active'],
                      ['good', 'Good', 'ri-heart-line', 'Minor conditions, well-managed'],
                      ['fair', 'Fair', 'ri-heart-2-line', 'Some health conditions'],
                      ['poor', 'Poor', 'ri-heart-add-line', 'Serious health conditions'],
                    ] as const).map(([val, label, icon, desc]) => (
                      <button
                        key={val}
                        onClick={() => set('health', val)}
                        className={`py-3 px-4 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer text-left ${inputs.health === val ? 'border-[#D97642] bg-[#FDF6F0] text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <div className="flex items-center gap-2 mb-0.5"><i className={icon} />{label}</div>
                        <div className="text-xs font-normal text-gray-400">{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 2: Income & Family ── */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Income &amp; Family</h2>
                <p className="text-gray-500 text-sm mb-8">Your income is the foundation of your coverage calculation.</p>

                <SliderInput label="Your Annual Income" value={inputs.annualIncome} min={20000} max={500000} step={5000} prefix="$" onChange={v => set('annualIncome', v)} hint="Before taxes. This determines how much your family needs to replace." />
                <SliderInput label="Spouse / Partner Annual Income" value={inputs.spouseIncome} min={0} max={300000} step={5000} prefix="$" onChange={v => set('spouseIncome', v)} hint="Enter $0 if single or stay-at-home spouse." />
                <SliderInput label="Years Until Retirement" value={inputs.yearsToRetirement} min={1} max={45} step={1} suffix=" yrs" onChange={v => set('yearsToRetirement', v)} hint="How many more years do you plan to work?" />

                <div className="mb-6">
                  <label className="text-sm font-semibold text-gray-700 block mb-3">Number of Dependent Children</label>
                  <div className="flex gap-3 flex-wrap">
                    {[0, 1, 2, 3, 4, 5, '6+'].map(n => (
                      <button
                        key={n}
                        onClick={() => set('numChildren', n === '6+' ? 6 : Number(n))}
                        className={`w-12 h-12 rounded-xl border-2 font-bold text-sm transition-all cursor-pointer ${inputs.numChildren === (n === '6+' ? 6 : Number(n)) ? 'border-[#D97642] bg-[#FDF6F0] text-[#D97642]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {inputs.numChildren > 0 && (
                  <SliderInput label="Age of Youngest Child" value={inputs.youngestChildAge} min={0} max={17} step={1} suffix=" yrs" onChange={v => set('youngestChildAge', v)} hint="Used to calculate how long your family needs income support." />
                )}
              </div>
            )}

            {/* ── Step 3: Debts ── */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Debts &amp; Obligations</h2>
                <p className="text-gray-500 text-sm mb-8">Your coverage should pay off all debts so your family starts fresh.</p>

                <SliderInput label="Mortgage Balance" value={inputs.mortgageBalance} min={0} max={800000} step={5000} prefix="$" onChange={v => set('mortgageBalance', v)} hint="Remaining balance on your home loan. Indiana average: $185,000." />
                <SliderInput label="Other Debts (Car, Credit Cards, Student Loans)" value={inputs.otherDebts} min={0} max={200000} step={1000} prefix="$" onChange={v => set('otherDebts', v)} hint="Add up all non-mortgage debts your family would inherit." />
                <SliderInput label="Final Expenses (Funeral, Estate, Medical)" value={inputs.finalExpenses} min={5000} max={50000} step={1000} prefix="$" onChange={v => set('finalExpenses', v)} hint="Indiana average funeral cost: $9,000-$14,000. Add estate/legal fees." />

                {/* Debt Summary */}
                <div className="bg-[#F9F7F4] rounded-xl p-5 mt-4">
                  <p className="text-sm font-bold text-gray-700 mb-3">Your Total Debt Obligations</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-600">Mortgage</span><span className="font-semibold">{fmt(inputs.mortgageBalance)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Other Debts</span><span className="font-semibold">{fmt(inputs.otherDebts)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Final Expenses</span><span className="font-semibold">{fmt(inputs.finalExpenses)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-gray-300 text-base font-bold text-[#1A3A52]">
                      <span>Total</span><span>{fmt(inputs.mortgageBalance + inputs.otherDebts + inputs.finalExpenses)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 4: Assets ── */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Assets &amp; Existing Coverage</h2>
                <p className="text-gray-500 text-sm mb-8">These reduce the amount of new coverage you need.</p>

                <SliderInput label="Savings &amp; Investments" value={inputs.savings} min={0} max={1000000} step={5000} prefix="$" onChange={v => set('savings', v)} hint="Include 401(k), IRA, savings accounts, and investments your family could access." />
                <SliderInput label="Existing Life Insurance Coverage" value={inputs.existingCoverage} min={0} max={2000000} step={25000} prefix="$" onChange={v => set('existingCoverage', v)} hint="Include employer group life + any personal policies you already have." />

                {/* Asset Summary */}
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mt-4">
                  <p className="text-sm font-bold text-teal-800 mb-3 flex items-center gap-2">
                    <i className="ri-shield-check-line" />Your Existing Financial Protection
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-600">Savings &amp; Investments</span><span className="font-semibold">{fmt(inputs.savings)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Existing Life Insurance</span><span className="font-semibold">{fmt(inputs.existingCoverage)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-teal-300 text-base font-bold text-teal-700">
                      <span>Total Assets</span><span>{fmt(inputs.savings + inputs.existingCoverage)}</span>
                    </div>
                  </div>
                </div>

                {inputs.existingCoverage > 0 && inputs.existingCoverage < inputs.annualIncome * 3 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                    <i className="ri-alert-line mr-2" />
                    Your current coverage ({fmt(inputs.existingCoverage)}) is only {Math.round(inputs.existingCoverage / inputs.annualIncome)}x your income — most Indiana families need 10-12x.
                  </div>
                )}
              </div>
            )}

            {/* ── Step 5: Goals ── */}
            {step === 5 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Goals</h2>
                <p className="text-gray-500 text-sm mb-8">Fine-tune your coverage based on what matters most to your family.</p>

                <SliderInput label="Years of Income to Replace" value={inputs.incomeReplaceYears} min={5} max={30} step={1} suffix=" yrs" onChange={v => set('incomeReplaceYears', v)} hint="How many years should your family be able to live on your income? Most experts recommend 10-15 years." />

                {inputs.numChildren > 0 && (
                  <SliderInput label="College Fund Per Child" value={inputs.educationPerChild} min={0} max={250000} step={10000} prefix="$" onChange={v => set('educationPerChild', v)} hint="Indiana public university (IU/Purdue): ~$100K. Private (Butler/Notre Dame): ~$220K. Community college: ~$16K." />
                )}

                {/* Preview */}
                <div className="bg-[#1A3A52] text-white rounded-xl p-6 mt-6">
                  <p className="text-sm font-bold text-white/60 mb-4 uppercase tracking-wider">Coverage Preview</p>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/70">Income Replacement ({inputs.incomeReplaceYears} yrs)</span>
                      <span className="font-bold">{fmt(inputs.annualIncome * inputs.incomeReplaceYears)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Debt Payoff</span>
                      <span className="font-bold">{fmt(inputs.mortgageBalance + inputs.otherDebts + inputs.finalExpenses)}</span>
                    </div>
                    {inputs.numChildren > 0 && (
                      <div className="flex justify-between">
                        <span className="text-white/70">Education Fund ({inputs.numChildren} child{inputs.numChildren > 1 ? 'ren' : ''})</span>
                        <span className="font-bold">{fmt(inputs.numChildren * inputs.educationPerChild)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t border-white/20 text-lg font-bold">
                      <span>Total Need</span>
                      <span className="text-[#D97642]">{fmt(inputs.annualIncome * inputs.incomeReplaceYears + inputs.mortgageBalance + inputs.otherDebts + inputs.finalExpenses + inputs.numChildren * inputs.educationPerChild)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Minus Existing Assets</span>
                      <span className="text-teal-400">-{fmt(inputs.savings + inputs.existingCoverage)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/20">
                      <span>Coverage Gap</span>
                      <span className="text-[#D97642]">
                        {fmt(Math.max(0, inputs.annualIncome * inputs.incomeReplaceYears + inputs.mortgageBalance + inputs.otherDebts + inputs.finalExpenses + inputs.numChildren * inputs.educationPerChild - inputs.savings - inputs.existingCoverage))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 6: Results ── */}
            {step === 6 && results && (
              <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-3xl" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Personalized Results</h2>
                  <p className="text-gray-500 text-sm">Based on your Indiana family profile — {inputs.age} yr old {inputs.gender}, {inputs.health} health, {inputs.numChildren} child{inputs.numChildren !== 1 ? 'ren' : ''}</p>
                </div>

                {/* Coverage Gap Hero */}
                <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-6 text-center">
                  <p className="text-white/60 text-sm mb-2 uppercase tracking-wider">Your Coverage Gap</p>
                  <p className="text-5xl font-bold text-[#D97642] mb-2">{fmt(results.coverageGap)}</p>
                  <p className="text-white/70 text-sm">This is how much additional life insurance your family needs</p>
                </div>

                {/* Breakdown */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#F9F7F4] rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Total Coverage Needed</p>
                    <p className="text-2xl font-bold text-gray-900">{fmt(results.totalNeed)}</p>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-5">
                    <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Existing Protection</p>
                    <p className="text-2xl font-bold text-teal-700">{fmt(results.existingAssets)}</p>
                  </div>
                </div>

                {/* Breakdown Bar */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                  <p className="text-sm font-bold text-gray-700 mb-4">Coverage Breakdown</p>
                  {[
                    { label: 'Income Replacement', value: results.incomeReplacement, color: 'bg-[#1A3A52]' },
                    { label: 'Debt Payoff', value: results.debtPayoff, color: 'bg-[#D97642]' },
                    { label: 'Education Fund', value: results.educationFund, color: 'bg-teal-500' },
                    { label: 'Final Expenses', value: results.finalExpenses, color: 'bg-gray-400' },
                  ].map(item => (
                    <div key={item.label} className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-semibold text-gray-900">{fmt(item.value)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className={`${item.color} h-2 rounded-full transition-all duration-700`}
                          style={{ width: `${Math.min(100, (item.value / results.totalNeed) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recommendation */}
                <div className="bg-[#FDF6F0] border-2 border-[#D97642] rounded-xl p-6 mb-6">
                  <p className="text-xs font-bold text-[#D97642] uppercase tracking-wider mb-3">Our Recommendation</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Policy Type</p>
                      <p className="font-bold text-gray-900 text-sm">{results.recommendedPolicy}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Term Length</p>
                      <p className="font-bold text-gray-900 text-sm">{results.recommendedTerm}-Year Term</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Rate Class</p>
                      <p className="font-bold text-gray-900 text-sm">{results.riskClass}</p>
                    </div>
                  </div>
                </div>

                {/* Premium Estimate */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 text-center">
                  <p className="text-sm text-gray-500 mb-2">Estimated Monthly Premium</p>
                  <p className="text-4xl font-bold text-[#1A3A52]">
                    ${results.estimatedMonthlyPremium}<span className="text-lg font-normal text-gray-400">/mo</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Estimate for {fmt(results.coverageGap)} {results.recommendedTerm}-yr term · {results.riskClass} class · Indiana rates
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Actual quotes may vary ±10-15%. Get a free exact quote below.</p>
                </div>

                {/* CTAs */}
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <Link
                    to="/instant-quote"
                    className="flex items-center justify-center gap-2 bg-[#D97642] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#C4663A] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-price-tag-3-line" />Get Exact Quotes
                  </Link>
                  <Link
                    to="/book-appointment"
                    className="flex items-center justify-center gap-2 bg-[#1A3A52] text-white px-6 py-4 rounded-xl font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap"
                  >
                    <i className="ri-calendar-line" />Talk to an Agent
                  </Link>
                </div>

                <button
                  onClick={restart}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer py-2"
                >
                  <i className="ri-refresh-line mr-1" />Start Over with Different Numbers
                </button>
              </div>
            )}

            {/* Navigation */}
            {step < 6 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={goBack}
                  disabled={step === 1}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer ${step === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <i className="ri-arrow-left-line" />Back
                </button>
                <span className="text-xs text-gray-400">Step {step} of 5</span>
                <button
                  onClick={goNext}
                  className="flex items-center gap-2 bg-[#D97642] text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-[#C4663A] transition-all cursor-pointer whitespace-nowrap"
                >
                  {step === 5 ? 'Calculate Results' : 'Continue'}
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            )}
          </div>

          {/* Trust Badges */}
          <div className="mt-6 flex flex-wrap justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><i className="ri-lock-line" />No data stored</span>
            <span className="flex items-center gap-1.5"><i className="ri-shield-check-line" />No spam, ever</span>
            <span className="flex items-center gap-1.5"><i className="ri-map-pin-line" />Indiana-specific rates</span>
            <span className="flex items-center gap-1.5"><i className="ri-user-line" />Licensed Indiana agent</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">How This Calculator Works</h2>
          <p className="text-gray-500 text-center mb-12 max-w-2xl mx-auto">We use the industry-standard DIME method, refined with Indiana-specific cost-of-living data and actuarial rate tables.</p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { letter: 'D', title: 'Debt', desc: 'Mortgage + car loans + credit cards + final expenses', color: 'bg-[#1A3A52]' },
              { letter: 'I', title: 'Income', desc: 'Annual income × years your family needs support', color: 'bg-[#D97642]' },
              { letter: 'M', title: 'Mortgage', desc: 'Remaining home loan balance (if not in Debt)', color: 'bg-teal-600' },
              { letter: 'E', title: 'Education', desc: 'College costs per child at Indiana universities', color: 'bg-gray-700' },
            ].map(item => (
              <div key={item.letter} className="text-center">
                <div className={`w-14 h-14 ${item.color} text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4`}>
                  {item.letter}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#F9F7F4] rounded-xl p-6 text-center">
            <p className="text-sm text-gray-600">
              <strong>D + I + M + E</strong> = Total Coverage Need &nbsp;|&nbsp; Minus your savings &amp; existing coverage = <strong>Your Coverage Gap</strong>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Calculator FAQ</h2>
          <div className="space-y-4">
            {[
              {
                q: 'How accurate is this calculator?',
                a: 'Very accurate for estimating your coverage need — the DIME method is the gold standard used by financial planners. Premium estimates are within 10-15% of actual carrier quotes for most Indiana residents. For a precise quote, use our instant quote tool or speak with Benjamin.',
              },
              {
                q: 'Is my information private?',
                a: 'Yes, 100%. All calculations happen entirely in your browser. No personal information is stored, transmitted, or shared. Your data never leaves your device unless you choose to request a quote.',
              },
              {
                q: 'Why does the calculator ask about my health?',
                a: 'Life insurance premiums are heavily influenced by your health class. A healthy 35-year-old non-smoker might pay $50/month for $1M in coverage, while someone with health conditions might pay $120/month for the same policy. Health class is the single biggest factor after age.',
              },
              {
                q: 'What if I already have life insurance through work?',
                a: 'Enter your employer coverage in Step 4 under "Existing Life Insurance." Most Indiana employers offer 1-2x salary — far below the 10-12x most families need. The calculator will show your gap and how much additional coverage you need.',
              },
              {
                q: 'Should I include my spouse\'s income in the calculation?',
                a: 'Yes — enter your spouse\'s income in Step 2. This affects how many years of income replacement your family actually needs. If your spouse earns $60K and you earn $80K, your family needs to replace $80K if you die, but they still have $60K coming in.',
              },
            ].map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Resources</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { to: '/resources/how-much-life-insurance-indiana-families-need', icon: 'ri-file-list-3-line', title: 'How Much Life Insurance Indiana Families Need', desc: 'Deep dive into the DIME method with real Indiana examples' },
              { to: '/resources/term-vs-whole-life-indianapolis-homeowners', icon: 'ri-scales-line', title: 'Term vs. Whole Life for Homeowners', desc: 'Which policy type fits your mortgage and family situation' },
              { to: '/life-insurance-rates-indianapolis', icon: 'ri-price-tag-3-line', title: 'Indianapolis Rate Comparison Tool', desc: 'Compare live rates from 5 top carriers by age and coverage' },
            ].map(item => (
              <Link key={item.to} to={item.to} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors text-sm">{item.title}</h3>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Close Your Coverage Gap?</h2>
          <p className="text-white/70 text-lg mb-8">Benjamin Wright helps Indiana families find the right coverage at the best rate — every day. Let's build your plan.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/instant-quote" className="inline-flex items-center justify-center gap-2 bg-[#D97642] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#C4663A] transition-colors whitespace-nowrap">
              <i className="ri-price-tag-3-line" />Get Free Quotes Now
            </Link>
            <a href="tel:3176482983" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap">
              <i className="ri-phone-line" />Call (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{question}</span>
        <i className={`ri-arrow-down-s-line text-gray-400 text-xl flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}
